<template>
  <div class="chat-container">
    <!-- 顶部导航栏 -->
    <div class="chat-header">
      <div class="header-title">
        实时对答
        <span class="connection-status" :class="{ connected: isConnected }" @click="!isConnected && connectWebSocket()">
          {{ isConnected ? '● 已连接' : '○ 未连接' }}
        </span>
      </div>
      <div class="photo-view-btn" @click="openPhotoDialog">拍照查看</div>
      <el-dropdown class="header-right" trigger="click" @command="handleCommand">
        <div class="dropdown-trigger">
          <i class="el-icon-more"></i>
        </div>
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item command="search">查找聊天内容</el-dropdown-item>
          <el-dropdown-item command="background">设置当前聊天背景</el-dropdown-item>
          <el-dropdown-item command="reconnect" v-if="!isConnected">重新连接</el-dropdown-item>
          <el-dropdown-item command="disconnect" v-if="isConnected">断开连接</el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </div>

    <!-- 搜索弹窗 -->
    <el-dialog
      :visible.sync="searchDialogVisible"
      width="100%"
      :modal-append-to-body="false"
      custom-class="search-dialog"
      :show-close="false"
    >
      <div class="search-container">
        <!-- 搜索头部 -->
        <div class="search-header">
          <div class="search-box">
            <i class="el-icon-search"></i>
            <input
              v-model="searchKeyword"
              type="text"
              placeholder="朱"
              ref="searchInput"
            />
            <i v-if="searchKeyword" class="el-icon-circle-close" @click="clearSearch"></i>
          </div>
          <div class="cancel-btn" @click="closeSearch">取消</div>
        </div>

        <!-- 搜索过滤标签 -->
        <div class="search-tabs">
          <div
            v-for="tab in searchTabs"
            :key="tab.value"
            class="tab-item"
            :class="{ active: activeTab === tab.value }"
            @click="activeTab = tab.value"
          >
            {{ tab.label }}
          </div>
        </div>

        <!-- 搜索结果 -->
        <div class="search-results">
          <div v-if="searchKeyword && filteredResults.length === 0" class="no-result">
            暂无搜索结果
          </div>
          <div v-else>
            <div
              v-for="(result, index) in filteredResults"
              :key="index"
              class="result-item"
            >
              <img :src="result.avatar" class="result-avatar" />
              <div class="result-content">
                <div class="result-name">{{ result.name }}</div>
                <div class="result-message" v-html="highlightKeyword(result.content)"></div>
              </div>
              <div class="result-date">{{ result.date }}</div>
            </div>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- 聊天消息区域 -->
    <div class="chat-messages" ref="messagesContainer">
      <div v-for="(message, index) in messages" :key="index" class="message-wrapper">
        <!-- 时间戳 -->
        <div v-if="message.showTime" class="message-time">{{ message.time }}</div>

        <!-- 消息内容 -->
        <div class="message-item" :class="message.type">
          <!-- 头像 -->
          <div class="avatar">
            <img :src="message.avatar" alt="avatar" />
          </div>

          <!-- 加载消息弹窗 -->
          <div v-if="message.isLoadingMessage" class="loading-message-card">
            <div class="loading-message-text">{{ message.loadingText }}</div>
            <div class="loading-message-gif">
              <img :src="loadingGif" alt="加载中..." />
            </div>
          </div>

          <!-- 普通消息气泡 -->
          <div v-else-if="!message.isRealTimeAnswer && !message.isPdfContent && !message.isOcrResult" class="message-bubble">
            <div class="bubble-content">{{ message.content }}</div>
          </div>

          <!-- PDF 内容卡片 -->
          <div v-else-if="message.isPdfContent" class="pdf-content-card">
            <div class="pdf-header">
              <i class="el-icon-document"></i>
              <span>学习计划 PDF</span>
            </div>
            <div class="pdf-info">
              <i class="el-icon-document-checked pdf-icon"></i>
              <div class="pdf-text">
                <div class="pdf-title">学习计划文档</div>
                <div class="pdf-desc">点击下方按钮查看或下载PDF文件</div>
              </div>
            </div>
            <div class="pdf-actions">
              <span @click="openPdfInNewWindow(message.pdfUrl)" class="pdf-action-btn">
                <i class="el-icon-view"></i>
                在新窗口打开
              </span>
              <span @click="downloadPdf(message.pdfUrl)" class="pdf-action-btn">
                <i class="el-icon-download"></i>
                下载PDF
              </span>
            </div>
          </div>

          <!-- OCR 识别结果卡片 (kind === 3) -->
          <div v-else-if="message.isOcrResult" class="ocr-result-card">
            <div class="ocr-header">
              <i class="el-icon-document"></i>
              <span>错题识别</span>
              <span v-if="message.processingTime" class="processing-time">
                ({{ (message.processingTime / 1000).toFixed(2) }}秒)
              </span>
              <!-- 操作按钮 -->
              <div class="ocr-actions">
                <span
                  v-if="message.shootImg"
                  class="action-btn"
                  @click="showShootImage(message.shootImg)"
                >
                  <i class="el-icon-picture-outline"></i>
                  拍摄图片
                </span>
                <span
                  v-if="message.id"
                  class="action-btn"
                  @click="showMoreInfo(message.id)"
                >
                  <i class="el-icon-info"></i>
                  更多信息
                </span>
              </div>
            </div>
            <div v-if="message.ocrData && message.ocrData.length > 0" class="ocr-items">
              <div class="ocr-items-title">
                <i class="el-icon-tickets"></i>
                识别的题目（共 {{ message.ocrData.length }} 题）
              </div>
              <div
                v-for="(item, idx) in message.ocrData"
                :key="idx"
                class="ocr-item"
              >
                <div class="ocr-item-number">{{ idx + 1 }}</div>
                <div class="ocr-text math-content" v-html="item.text"></div>
                <div class="ocr-image-icon" @click="showOcrImage(item.paperCutImg)">
                  <i class="el-icon-picture-outline"></i>
                </div>
              </div>
            </div>
            <div v-else class="ocr-empty">
              <i class="el-icon-document-checked"></i>
              <p>错题搜集完成，暂无识别内容</p>
            </div>
            <!-- 打印按钮 -->
            <div v-if="message.ocrData && message.ocrData.length > 0" class="ocr-footer">
              <div class="print-btns">
                <span class="print-btn" @click="handlePrintCommand('1', message.id)">
                  <i class="el-icon-printer"></i>
                  打印当前错题
                </span>
                <span class="print-btn" @click="handlePrintCommand('2', message.id)">
                  <i class="el-icon-printer"></i>
                  打印类似错题
                </span>
              </div>
            </div>
          </div>

          <!-- 实时解答卡片 -->
          <div v-else-if="message.isRealTimeAnswer" class="answer-card" :ref="'answerCard_' + index">
            <div class="answer-header">
              <i class="el-icon-edit-outline"></i>
              <span>实时解答</span>
              <span v-if="message.answerData.processingTime" class="processing-time">
                ({{ (message.answerData.processingTime / 1000).toFixed(2) }}秒)
              </span>
              <!-- 操作按钮 -->
              <div class="answer-actions">
                <span
                  v-if="message.answerData.ShootImg"
                  class="action-btn"
                  @click="showShootImage(message.answerData.ShootImg)"
                >
                  <i class="el-icon-picture-outline"></i>
                  拍摄图片
                </span>
                <span
                  v-if="message.id"
                  class="action-btn"
                  @click="showMoreInfo(message.id)"
                >
                  <i class="el-icon-info"></i>
                  更多信息
                </span>
              </div>
            </div>

            <!-- 题目内容 -->
            <div class="answer-section question-section">
              <div class="section-title">
                <i class="el-icon-document"></i>
                题目
              </div>
              <div class="section-content math-content" v-html="message.answerData.ocrContent"></div>
            </div>

            <!-- 用户答案和正确答案 -->
            <div class="answer-result" :class="{ error: !message.answerData.isTure }">
              <div class="result-item">
                <i :class="message.answerData.isTure ? 'el-icon-success' : 'el-icon-error'"></i>
                <span class="result-label">{{ message.answerData.msg }}</span>
              </div>
              <div class="result-item correct-answer" v-if="message.answerData.answer">
                <span class="result-label">正确答案：</span>
                <span class="answer-value">{{ message.answerData.answer }}</span>
              </div>
            </div>

            <!-- 考点 -->
            <div class="answer-section knowledge-section" v-if="message.answerData.testPoints">
              <div class="section-title">
                <i class="el-icon-star-on"></i>
                考点
              </div>
              <div class="section-content math-content" v-html="message.answerData.testPoints"></div>
            </div>

            <!-- 解析 -->
            <div class="answer-section analysis-section" v-if="message.answerData.preAnswer">
              <div class="section-title">
                <i class="el-icon-reading"></i>
                解析
              </div>
              <div class="section-content math-content" v-html="message.answerData.preAnswer"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- OCR 图片预览弹窗 -->
    <el-dialog
      :visible.sync="ocrImageDialogVisible"
      width="90%"
      custom-class="ocr-image-dialog"
      :show-close="true"
      append-to-body
    >
      <img :src="currentOcrImage" alt="题目图片" class="ocr-preview-image" />
    </el-dialog>

    <!-- 拍摄图片预览弹窗 -->
    <el-dialog
      :visible.sync="shootImageDialogVisible"
      width="90%"
      custom-class="shoot-image-dialog"
      :show-close="true"
      append-to-body
    >
      <img :src="currentShootImage" alt="拍摄图片" class="shoot-preview-image" />
    </el-dialog>

    <!-- 更多信息弹窗 -->
    <el-dialog
      :visible.sync="moreInfoDialogVisible"
      width="80%"
      title="更多信息"
      custom-class="more-info-dialog"
      :show-close="true"
      append-to-body
    >
      <div class="more-info-content">
        <div class="info-item">
          <span class="info-label">记录ID：</span>
          <span class="info-value">{{ currentRecordId }}</span>
        </div>
      </div>
    </el-dialog>

    <!-- 拍照查看弹窗 -->
    <photo-dialog :visible.sync="photoDialogVisible" :dev-no="devNo" />

    <!-- 底部输入栏 -->
    <div class="chat-input" v-show="!showLoading">
      <div class="input-icon" @click="showVoice">
        <i class="el-icon-microphone"></i>
      </div>
      <div class="input-box">
        <input
          v-model="inputMessage"
          type="text"
          placeholder="请输入消息..."
          @keyup.enter="sendMessage"
        />
      </div>
      <div class="input-icon" @click="showEmoji">
        <i class="el-icon-smile"></i>
      </div>
      <div class="input-icon" @click="showMore">
        <i class="el-icon-circle-plus"></i>
      </div>
    </div>

    <!-- Loading 区域 -->
    <div class="loading-container" v-show="showLoading">
      <img :src="loadingGif" alt="加载中..." class="loading-gif" />
    </div>
  </div>
</template>

<script>
import { wsConfig, getDevNo } from '@/config/axios'
import axios from 'axios'
import PhotoDialog from '@/components/PhotoDialog.vue'

export default {
  name: 'StudentPrint',
  components: {
    PhotoDialog
  },
  data() {
    return {
      inputMessage: '',
      searchDialogVisible: false,
      searchKeyword: '',
      activeTab: 'all',
      searchTabs: [
        { label: '全部', value: 'all' },
        { label: '图片与视频', value: 'media' },
        { label: '文件', value: 'file' },
        { label: '链接', value: 'link' },
        { label: '音乐与音频', value: 'audio' }
      ],
      // WebSocket 相关
      websocket: null,
      isConnected: false,
      serverUrl: wsConfig.serverUrl,
      devNo: '',
      messages: [],
      // 头像
      robotAvatar: require('@/assets/img/okaka.png'),
      userAvatar: require('@/assets/img/head.png'),
      // loading 状态
      showLoading: false,
      loadingGif: require('@/assets/img/load.gif'),
      // MathJax 相关
      mathJaxLoaded: false,
      // OCR 图片预览
      ocrImageDialogVisible: false,
      currentOcrImage: '',
      // 拍摄图片预览
      shootImageDialogVisible: false,
      currentShootImage: '',
      // 更多信息
      moreInfoDialogVisible: false,
      currentRecordId: '',
      // 拍照查看
      photoDialogVisible: false
    }
  },
  computed: {
    filteredResults() {
      if (!this.searchKeyword) {
        return []
      }
      // 从实际消息中搜索
      return this.messages.filter(msg => {
        const content = msg.content || ''
        return content.includes(this.searchKeyword)
      })
    }
  },
  methods: {
    // 加载 MathJax
    loadMathJax() {
      if (this.mathJaxLoaded || window.MathJax) {
        this.mathJaxLoaded = true
        return Promise.resolve()
      }

      return new Promise((resolve) => {
        window.MathJax = {
          tex: {
            inlineMath: [['$', '$'], ['\\(', '\\)']],
            displayMath: [['$$', '$$'], ['\\[', '\\]']],
            processEscapes: true,
            processEnvironments: true,
            // 加载所有包
            packages: {'[+]': ['base', 'ams']},
            // 遇到未定义的命令时的处理
            macros: {},
            tags: 'ams'
          },
          svg: {
            fontCache: 'global'
          },
          options: {
            skipHtmlTags: ['script', 'noscript', 'style', 'textarea', 'pre', 'code'],
            processHtmlClass: 'math-content',
            ignoreHtmlClass: 'no-mathjax',
            // 启用详细的错误信息
            enableMenu: true
          },
          startup: {
            ready: () => {
              window.MathJax.startup.defaultReady()
              this.mathJaxLoaded = true
              resolve()
            }
          },
          // 注意：不要在这里覆盖 MathJax.typesetPromise（MathJax 会在加载后注入真正的方法）
        }

        const script = document.createElement('script')
        // 同时支持 TeX（$...$）与 MathML（<math>...</math>），避免 Math input error
        script.src = 'https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-mml-chtml.js'
        script.async = true
        script.onerror = () => {
          console.error('MathJax 脚本加载失败')
        }
        document.head.appendChild(script)
      })
    },

    // 渲染数学公式
    async renderMath() {
      await this.loadMathJax()
      if (window.MathJax && window.MathJax.typesetPromise) {
        await this.$nextTick()
        // 额外延迟确保 DOM 完全更新
        await new Promise(resolve => setTimeout(resolve, 100))
        try {
          const mathElements = this.$el.querySelectorAll('.math-content')
          if (mathElements.length > 0) {
            await window.MathJax.typesetClear(Array.from(mathElements))
            await window.MathJax.typesetPromise(Array.from(mathElements))
          }
        } catch (err) {
          console.error('MathJax 渲染错误:', err)
        }
      }
    },

    // 规范化 OCR/解析返回的 HTML，保证 MathJax 能稳定渲染（支持 LaTeX + MathML）
    cleanMathML(htmlString) {
      if (!htmlString) return htmlString

      let cleaned = htmlString

      // 0) 将“展示公式”（独占一行）统一转为“行内公式”
      //    目标：避免 MathJax 生成 mjx-container[display="true"] 导致居中/独占一行
      //    $$ ... $$ / \[ ... \] -> $ ... $
      cleaned = cleaned
        .replace(/\$\$([\s\S]*?)\$\$/g, (full, inner) => {
          const content = String(inner).replace(/\s+/g, ' ').trim()
          return `$${content}$`
        })
        .replace(/\\\[([\s\S]*?)\\\]/g, (full, inner) => {
          const content = String(inner).replace(/\s+/g, ' ').trim()
          return `$${content}$`
        })

      // 1) 去掉多余的 <html>/<body> 包裹，避免 v-html 注入后 DOM 结构异常
      cleaned = cleaned.replace(/<\/?(html|body)[^>]*>/gi, '')

      // 2) OCR 输出里常见 \" 多余转义：把 \" 还原成 "
      //    这能把 <div class=\"line\"> 修复成标准 HTML，避免后续处理/渲染异常
      cleaned = cleaned.replace(/\\"/g, '"')

      // 3) 移除 data-bbox（有时是 "..." / '...' / \"...\" 三种形式）
      cleaned = cleaned.replace(/\s+data-bbox=(?:"[^"]*"|'[^']*'|\\"[^\\"]*\\")/g, '')

      // 4) 修正常见的 LaTeX 命令“多一层反斜杠”的情况：\\frac -> \frac（仅对字母命令）
      //    避免 MathJax 将 \\ 当作换行命令导致解析错误
      cleaned = cleaned.replace(/\$([\s\S]*?)\$/g, (full, inner) => {
        const fixedInner = String(inner).replace(/\\\\(?=[a-zA-Z])/g, '\\')
        return `$${fixedInner}$`
      })

      // 5) 移除多余的空行/空白拼接
      cleaned = cleaned.replace(/(<\/div>)\s*(<div)/g, '$1$2')

      // 确保 LaTeX 公式中的反斜杠没有被转义
      // 检查是否存在被 HTML 转义的情况，如 &lt; &gt; &amp;
      if (cleaned.includes('&lt;') || cleaned.includes('&gt;') || cleaned.includes('&amp;')) {
        const textarea = document.createElement('textarea')
        textarea.innerHTML = cleaned
        cleaned = textarea.value
      }

      return cleaned
    },

    // 解析实时解答数据（用于 messageType === 2）
    parseRealTimeAnswerData(jsonString) {
      try {
        // 先从原始字符串中提取 answerStr 的值（保持"外层 JSON 字符串"里的原始转义）
        const answerStrMatch = jsonString.match(/"answerStr"\s*:\s*"((?:[^"\\]|\\.)*)"/s)
        if (!answerStrMatch) {
          console.error('无法从原始字符串中提取 answerStr')
          return null
        }

        const rawAnswerStr = answerStrMatch[1]

        // 外层 JSON 仍然正常解析，用来取 ShootImg / requestAnswerRecordId / processingTime
        const outerData = JSON.parse(jsonString)

        // 将 rawAnswerStr 按"JSON 字符串字面量"规则解码成真正的 inner JSON 文本
        // 注意：此处会把 \n 变成真实换行，这在 JSON 字符串值里是非法的，所以后面还要二次修复
        const decodedAnswerStr = JSON.parse(`"${rawAnswerStr}"`)

        // 实时解答专用：修复 inner JSON 文本中「字符串内部」的裸换行/控制字符 & 非法转义（如 \{）
        const sanitizeInnerJsonText = (text) => {
          let out = ''
          let inString = false
          let escape = false

          for (let i = 0; i < text.length; i++) {
            const ch = text[i]

            if (!inString) {
              if (ch === '"') {
                inString = true
              }
              out += ch
              continue
            }

            // inString === true
            if (escape) {
              // 仅允许 JSON 标准转义：" \ / b f n r t u
              if (ch === 'u') {
                const hex = text.slice(i + 1, i + 5)
                if (/^[0-9a-fA-F]{4}$/.test(hex)) {
                  out += '\\u' + hex
                  i += 4
                } else {
                  // 非法 unicode，保守处理：把反斜杠也转义掉
                  out += '\\\\u'
                }
              } else if ('"\\/bfnrt'.includes(ch)) {
                out += '\\' + ch
              } else {
                // 非法转义（如 \{），把反斜杠本身转义，保留原字符
                out += '\\\\' + ch
              }
              escape = false
              continue
            }

            if (ch === '\\') {
              escape = true
              continue
            }

            if (ch === '"') {
              inString = false
              out += ch
              continue
            }

            // JSON 字符串内部不能出现裸控制字符
            if (ch === '\n') {
              out += '\\n'
              continue
            }
            if (ch === '\r') {
              out += '\\r'
              continue
            }
            if (ch === '\t') {
              out += '\\t'
              continue
            }
            const code = ch.charCodeAt(0)
            if (code <= 0x1F) {
              // 其他控制字符统一剔除（也可以改成 \\u00XX）
              continue
            }

            out += ch
          }

          // 若字符串以反斜杠结尾，补上一个反斜杠避免 JSON.parse 再次报错
          if (escape) out += '\\\\'
          return out
        }

        let processedAnswerStr = sanitizeInnerJsonText(decodedAnswerStr)

        // 移除 data-bbox 属性（兼容 "..."）
        processedAnswerStr = processedAnswerStr.replace(/\s+data-bbox="[^"]*"/g, '')

        const innerData = JSON.parse(processedAnswerStr)
        
        // 清理 innerData 中的 MathML 内容
        if (innerData) {
          if (innerData.ocrContent) {
            innerData.ocrContent = this.cleanMathML(innerData.ocrContent)
          }

          if (innerData.testPoints) {
            innerData.testPoints = this.cleanMathML(innerData.testPoints)
          }

          if (innerData.preAnswer) {
            innerData.preAnswer = this.cleanMathML(innerData.preAnswer)
          }

          // 将外层数据添加到内层数据中
          innerData.ShootImg = outerData.ShootImg
          innerData.requestAnswerRecordId = outerData.requestAnswerRecordId
          innerData.processingTime = outerData.processingTime
        }

        return innerData
      } catch (e) {
        console.error('解析实时解答数据失败:', e.message)
        return null
      }
    },

    // 解析双层 JSON 数据（用于 kind === 3 OCR识别结果）
    parseDoubleJsonData(jsonString) {
      try {
        const outerData = JSON.parse(jsonString)
        if (outerData && outerData.answerStr) {
          // 移除 data-bbox 属性
          let cleanedAnswerStr = outerData.answerStr.replace(/\s+data-bbox="[^"]*"/g, '')

          const innerData = JSON.parse(cleanedAnswerStr)

          // 清理 innerData 中的 MathML 内容
          if (innerData) {
            if (innerData.ocrContent) {
              innerData.ocrContent = this.cleanMathML(innerData.ocrContent)
            }

            if (innerData.testPoints) {
              innerData.testPoints = this.cleanMathML(innerData.testPoints)
            }

            if (innerData.preAnswer) {
              innerData.preAnswer = this.cleanMathML(innerData.preAnswer)
            }

            // 处理 OCR 数组数据
            if (Array.isArray(innerData)) {
              innerData.forEach(item => {
                if (item.text) {
                  item.text = this.cleanMathML(item.text)
                }
              })
            }
          }

          return { outerData, innerData }
        }
        return { outerData, innerData: null }
      } catch (e) {
        console.error('解析双层 JSON 数据失败:', e)
        return { outerData: null, innerData: null }
      }
    },

    // 创建消息对象（统一消息格式）
    createMessageObject(type, content, time, extraProps = {}) {
      return {
        type,
        content,
        time,
        showTime: extraProps.showTime || false,
        avatar: type === 'sent' ? this.userAvatar : this.robotAvatar,
        ...extraProps
      }
    },

    // 解析不同类型的消息内容
    parseMessageContent(answerContent, messageType, time, chatHistoryId) {
      if (!answerContent) return null

      if (messageType === 2) {
        // 实时解答
        const answerData = this.parseRealTimeAnswerData(answerContent)
        if (answerData) {
          return this.createMessageObject('received', answerContent, time, {
            id: chatHistoryId,
            isRealTimeAnswer: true,
            answerData: answerData
          })
        }
      } else if (messageType === 3) {
        // OCR 识别结果
        const { outerData, innerData } = this.parseDoubleJsonData(answerContent)
        if (outerData) {
          // 如果 innerData 是数组且有内容，正常显示
          if (Array.isArray(innerData) && innerData.length > 0) {
            return this.createMessageObject('received', answerContent, time, {
              id: chatHistoryId,
              isOcrResult: true,
              ocrData: innerData,
              shootImg: outerData.ShootImg,
              requestErrorRecordId: outerData.requestErrorRecordId,
              processingTime: outerData.processingTime
            })
          }
          // 如果 innerData 为空或不是数组，显示为普通消息（带拍摄图片）
          return this.createMessageObject('received', '错题搜集完成', time, {
            id: chatHistoryId,
            isOcrResult: true,
            ocrData: [],
            shootImg: outerData.ShootImg,
            requestErrorRecordId: outerData.requestErrorRecordId,
            processingTime: outerData.processingTime
          })
        }
      } else if (messageType === 4) {
        // 学习计划 PDF
        return this.createMessageObject('received', answerContent, time, {
          id: chatHistoryId,
          isPdfContent: true,
          pdfUrl: answerContent
        })
      }

      // 普通消息
      return this.createMessageObject('received', answerContent, time, {
        id: chatHistoryId
      })
    },

    // 滚动到底部并渲染数学公式
    scrollAndRenderMath() {
      this.$nextTick(() => {
        this.scrollToBottom()
        this.renderMath()
      })
    },

    // 获取聊天历史记录
    async fetchChatHistory() {
      try {
        const response = await axios.get('chatHistory', {
          params: {
            pageNumber: 0,
            pageSize: 50,
            devNo: this.devNo
          }
        })

        if (response.data.code === 200 && response.data.data.list) {
          const chatList = response.data.data.list
          const messages = []

          // 先反转列表，让旧消息在前（接口返回的是降序）
          chatList.reverse().forEach((item, index) => {
            const time = item.createTime ? item.createTime.substring(11, 16) : ''

            // 添加用户问题（右边）
            if (item.chatContent) {
              messages.push(this.createMessageObject('sent', item.chatContent, time, {
                showTime: index === 0
              }))
            }

            // 添加机器人回答（左边），使用统一的消息解析方法
            if (item.answerContent) {
              const parsedMessage = this.parseMessageContent(item.answerContent, item.type, time, item.id)
              if (parsedMessage) {
                messages.push(parsedMessage)
              }
            }
          })

          this.messages = messages
          this.scrollAndRenderMath()
        }
      } catch (error) {
        console.error('获取聊天历史失败:', error)
        this.$message.error('获取聊天历史失败')
      }
    },
    handleCommand(command) {
      if (command === 'search') {
        this.openSearch()
      } else if (command === 'background') {
        this.$message.info('设置聊天背景功能')
      } else if (command === 'reconnect') {
        this.connectWebSocket()
      } else if (command === 'disconnect') {
        this.disconnectWebSocket()
      }
    },
    openSearch() {
      this.searchDialogVisible = true
      this.$nextTick(() => {
        this.$refs.searchInput && this.$refs.searchInput.focus()
      })
    },
    closeSearch() {
      this.searchDialogVisible = false
      this.searchKeyword = ''
      this.activeTab = 'all'
    },
    clearSearch() {
      this.searchKeyword = ''
    },
    highlightKeyword(text) {
      if (!this.searchKeyword) {
        return text
      }
      const keyword = this.searchKeyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      return text.replace(new RegExp(keyword, 'gi'), match => `<span class="highlight">${match}</span>`)
    },
    sendMessage() {
      if (this.inputMessage.trim()) {
        this.messages.push(this.createMessageObject('sent', this.inputMessage, this.getCurrentTime()))
        this.inputMessage = ''
        this.scrollToBottom()
      }
    },
    getCurrentTime() {
      const now = new Date()
      return `${now.getHours()}:${String(now.getMinutes()).padStart(2, '0')}`
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer
        if (container) {
          container.scrollTop = container.scrollHeight
        }
      })
    },
    showVoice() {
      this.$message.info('语音功能')
    },
    showEmoji() {
      this.$message.info('表情功能')
    },
    showMore() {
      this.$message.info('更多功能')
    },
    // WebSocket 方法
    connectWebSocket() {
      if (!this.serverUrl) {
        this.$message.error('请输入服务器地址')
        return
      }

      if (!this.devNo) {
        this.$message.error('请输入设备号')
        return
      }

      // 断开之前的连接
      if (this.websocket !== null) {
        this.websocket.close()
      }

      // 创建WebSocket连接，将设备号作为URL参数
      const url = `${this.serverUrl}?devNo=${this.devNo}`

      try {
        this.websocket = new WebSocket(url)

        // 连接建立时触发
        this.websocket.onopen = () => {
          this.isConnected = true
          this.$message.success('连接成功')
        }

        // 接收到消息时触发
        this.websocket.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data)
            this.handleWebSocketMessage(message)
          } catch (e) {
            // 非 JSON 消息不处理
          }
        }

        // 连接关闭时触发
        this.websocket.onclose = () => {
          this.isConnected = false
          this.$message.warning('连接已断开')
        }

        // 发生错误时触发
        this.websocket.onerror = () => {
          this.$message.error('连接失败，请检查服务器地址和网络连接')
        }
      } catch (e) {
        this.$message.error(`连接失败: ${e.message}`)
      }
    },
    disconnectWebSocket() {
      if (this.websocket !== null) {
        this.websocket.close()
        this.websocket = null
        this.isConnected = false
      }
    },
    showOcrImage(imageUrl) {
      this.currentOcrImage = imageUrl
      this.ocrImageDialogVisible = true
    },
    showShootImage(imageUrl) {
      this.currentShootImage = imageUrl
      this.shootImageDialogVisible = true
    },
    showMoreInfo(recordId) {
      this.currentRecordId = recordId
      this.moreInfoDialogVisible = true
    },
    // 在新窗口打开PDF
    openPdfInNewWindow(pdfUrl) {
      if (pdfUrl) {
        window.open(pdfUrl, '_blank')
      }
    },
    // 下载PDF
    downloadPdf(pdfUrl) {
      if (pdfUrl) {
        const link = document.createElement('a')
        link.href = pdfUrl
        link.download = 'study-plan.pdf'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    },
    // 移除最后一个加载消息
    removeLoadingMessage() {
      for (let i = this.messages.length - 1; i >= 0; i--) {
        if (this.messages[i].isLoadingMessage) {
          this.messages.splice(i, 1)
          break
        }
      }
    },
    handleWebSocketMessage(message) {
      if (message.type === 'WAKE') {
        // 唤醒状态：显示 loading，隐藏输入框
        this.showLoading = true
      } else if (message.type === 'CLOSE') {
        // 关闭状态：隐藏 loading，显示输入框
        this.showLoading = false
      } else if (message.type === 'INFO-R') {
        // 将消息显示在右边
        this.messages.push(this.createMessageObject('sent', message.message, this.getCurrentTime()))

        // 如果 kind >= 1，在左边添加一个临时的加载消息
        if (message.kind >= 1) {
          const loadingTextMap = {
            1: '正在解答中',
            2: '实时解答',
            3: '错题搜集',
            4: '打印中'
          }
          this.messages.push(this.createMessageObject('received', '', this.getCurrentTime(), {
            isLoadingMessage: true,
            loadingText: loadingTextMap[message.kind] || '处理中'
          }))
        }

        this.scrollToBottom()
      } else if (message.type === 'INFO-L') {
        // 移除之前添加的临时加载消息
        this.removeLoadingMessage()

        // 使用统一的消息解析方法
        const parsedMessage = this.parseMessageContent(message.message, message.kind, this.getCurrentTime(), message.id)
        if (parsedMessage) {
          this.messages.push(parsedMessage)
          // 需要渲染数学公式的消息类型
          if (message.kind === 2 || message.kind === 3) {
            this.scrollAndRenderMath()
          } else {
            this.scrollToBottom()
          }
        }
      }
    },
    // 打开拍照弹窗
    openPhotoDialog() {
      this.photoDialogVisible = true
    },
    // 处理打印命令
    async handlePrintCommand(type, chatHistoryId) {
      try {
        const response = await axios.post('device/command/print', null, {
          params: {
            type: type,
            devNo: this.devNo,
            chatHistoryId: chatHistoryId
          }
        })
        if (response.data.code === 200) {
          this.$message.success(type === '1' ? '打印当前成功' : '打印类似成功')
        } else {
          this.$message.error(response.data.message || '打印失败')
        }
      } catch (error) {
        console.error('打印失败:', error)
        this.$message.error('打印失败，请稍后重试')
      }
    }
  },
  async mounted() {
    this.devNo = await getDevNo()
    // 获取聊天历史
    this.fetchChatHistory()
    // 自动连接 WebSocket
    this.connectWebSocket()
  },
  beforeDestroy() {
    // 组件销毁前断开 WebSocket 连接
    this.disconnectWebSocket()
  }
}
</script>

<style lang="less" scoped>
.chat-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: #ededed;
}

// 顶部导航栏
.chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 60px;
  background-color: #f7f7f7;
  border-bottom: 1px solid #d9d9d9;
  padding: 0 16px;

  .header-right {
    width: 40px;
    height: 40px;

    .dropdown-trigger {
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;

      i {
        font-size: 24px;
        color: #000;
      }
    }
  }

  .header-title {
    flex: 1;
    text-align: center;
    font-size: 16px;
    font-weight: 500;
    color: #000;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    .connection-status {
      font-size: 12px;
      color: #999;
      font-weight: normal;

      &.connected {
        color: #07c160;
      }
    }
  }

  .photo-view-btn {
    padding: 8px 16px;
    font-size: 14px;
    color: #576b95;
    background-color: #f0f2f5;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.3s;

    &:hover {
      background-color: #e0e4ea;
      color: #07c160;
    }

    &:active {
      transform: scale(0.95);
    }
  }
}

// 消息区域
.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #c0c0c0;
    border-radius: 2px;
  }
}

.message-wrapper {
  margin-bottom: 16px;
}

// 时间戳
.message-time {
  text-align: center;
  color: #999;
  font-size: 12px;
  margin: 12px 0;
}

// 消息项
.message-item {
  display: flex;
  margin-bottom: 12px;

  &.received {
    justify-content: flex-start;

    .message-bubble {
      background-color: #fff;
      margin-left: 10px;

      &::before {
        content: '';
        position: absolute;
        left: -8px;
        top: 12px;
        width: 0;
        height: 0;
        border-style: solid;
        border-width: 5px 8px 5px 0;
        border-color: transparent #fff transparent transparent;
      }
    }
  }

  &.sent {
    justify-content: flex-end;

    .avatar {
      order: 2;
    }

    .message-bubble {
      background-color: #95ec69;
      margin-right: 10px;

      &::after {
        content: '';
        position: absolute;
        right: -8px;
        top: 12px;
        width: 0;
        height: 0;
        border-style: solid;
        border-width: 5px 0 5px 8px;
        border-color: transparent transparent transparent #95ec69;
      }
    }
  }
}

// 头像
.avatar {
  width: 40px;
  height: 40px;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    border-radius: 4px;
    object-fit: cover;
  }
}

// 消息气泡
.message-bubble {
  position: relative;
  max-width: 60%;
  padding: 10px 14px;
  border-radius: 6px;
  word-wrap: break-word;

  .bubble-content {
    font-size: 16px;
    line-height: 1.4;
    color: #000;
  }
}

// 加载消息卡片
.loading-message-card {
  position: relative;
  background-color: #fff;
  border-radius: 8px;
  padding: 20px 24px;
  margin-left: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  text-align: center;
  min-width: 160px;

  &::before {
    content: '';
    position: absolute;
    left: -8px;
    top: 12px;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 5px 8px 5px 0;
    border-color: transparent #fff transparent transparent;
  }

  .loading-message-text {
    font-size: 15px;
    color: #333;
    margin-bottom: 12px;
    font-weight: 500;
  }

  .loading-message-gif {
    display: flex;
    justify-content: center;
    align-items: center;

    img {
      width: 40px;
      height: 40px;
      object-fit: contain;
    }
  }
}

// PDF 内容卡片（学习计划等）
.pdf-content-card {
  position: relative;
  width: 85%;
  background-color: #fff;
  border-radius: 8px;
  margin-left: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    left: -8px;
    top: 12px;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 5px 8px 5px 0;
    border-color: transparent #fff transparent transparent;
  }

  .pdf-header {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 12px 16px;
    border-bottom: 1px solid #e5e5e5;
    color: #f56c6c;
    font-size: 14px;
    font-weight: 500;

    i {
      font-size: 16px;
    }
  }

  .pdf-info {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 24px 16px;
    background-color: #f5f7fa;

    .pdf-icon {
      font-size: 48px;
      color: #409eff;
      flex-shrink: 0;
    }

    .pdf-text {
      flex: 1;

      .pdf-title {
        font-size: 16px;
        color: #303133;
        font-weight: 500;
        margin-bottom: 6px;
      }

      .pdf-desc {
        font-size: 13px;
        color: #909399;
      }
    }
  }

  .pdf-actions {
    display: flex;
    gap: 12px;
    padding: 12px 16px;
    border-top: 1px solid #e5e5e5;
    background-color: #fafafa;

    .pdf-action-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 8px 16px;
      font-size: 14px;
      color: #fff;
      background-color: #409eff;
      border-radius: 4px;
      cursor: pointer;
      text-decoration: none;
      transition: all 0.3s;

      i {
        font-size: 14px;
      }

      &:hover {
        background-color: #66b1ff;
      }

      &:active {
        transform: scale(0.95);
      }
    }
  }
}

// 实时解答卡片
.answer-card {
  position: relative;
  max-width: 75%;
  background-color: #fff;
  border-radius: 8px;
  padding: 16px;
  margin-left: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &::before {
    content: '';
    position: absolute;
    left: -8px;
    top: 12px;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 5px 8px 5px 0;
    border-color: transparent #fff transparent transparent;
  }

  .answer-header {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid #e5e5e5;
    color: #07c160;
    font-size: 14px;
    font-weight: 500;

    i {
      font-size: 16px;
    }

    .processing-time {
      font-size: 12px;
      color: #999;
      font-weight: normal;
      margin-left: 4px;
    }

    .answer-actions {
      margin-left: auto;
      display: flex;
      gap: 12px;

      .action-btn {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 4px 10px;
        font-size: 13px;
        color: #576b95;
        background-color: #f0f2f5;
        border-radius: 4px;
        cursor: pointer;
        transition: all 0.3s;

        i {
          font-size: 14px;
        }

        &:hover {
          background-color: #e0e4ea;
          color: #07c160;
        }

        &:active {
          transform: scale(0.95);
        }
      }
    }
  }

  .answer-section {
    margin-bottom: 12px;
    padding: 12px;
    border-radius: 6px;
    background-color: #f9f9f9;

    &:last-child {
      margin-bottom: 0;
    }

    &.question-section {
      background-color: #fff7e6;
      border-left: 3px solid #faad14;
    }

    &.knowledge-section {
      background-color: #e6f7ff;
      border-left: 3px solid #1890ff;
    }

    &.analysis-section {
      background-color: #f6ffed;
      border-left: 3px solid #52c41a;
    }

    .section-title {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 14px;
      color: #333;
      margin-bottom: 8px;
      font-weight: 600;

      i {
        font-size: 16px;
      }
    }

    .section-content {
      font-size: 14px;
      line-height: 1.8;
      color: #333;

      /deep/ p {
        margin: 6px 0;
      }

      /deep/ ul {
        margin: 8px 0;
        padding-left: 20px;
      }

      /deep/ li {
        margin: 4px 0;
      }

      // MathJax 公式样式
      &.math-content {
        /deep/ .MathJax {
          font-size: 100% !important;
        }

        /deep/ mjx-container {
          margin: 4px 0;
          overflow-x: auto;
          overflow-y: hidden;
        }

        /deep/ mjx-container[display="true"] {
          display: block;
          margin: 10px 0;
          text-align: center;
        }
      }
    }
  }

  .answer-result {
    margin-bottom: 12px;
    padding: 12px;
    background-color: #e8f8ed;
    border-radius: 6px;
    border-left: 4px solid #07c160;

    &.error {
      background-color: #fef0f0;
      border-left-color: #f56c6c;

      .result-label {
        color: #f56c6c;
      }

      i {
        color: #f56c6c;
      }
    }

    .result-item {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 4px;

      &:last-child {
        margin-bottom: 0;
      }

      i {
        font-size: 16px;
        color: #07c160;
      }
    }

    .result-label {
      font-size: 14px;
      color: #07c160;
      font-weight: 600;
    }

    .answer-value {
      font-size: 14px;
      color: #333;
      font-weight: 500;
    }
  }
}

// OCR 识别结果卡片
.ocr-result-card {
  position: relative;
  max-width: 85%;
  background-color: #fff;
  border-radius: 8px;
  padding: 16px;
  margin-left: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &::before {
    content: '';
    position: absolute;
    left: -8px;
    top: 12px;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 5px 8px 5px 0;
    border-color: transparent #fff transparent transparent;
  }

  .ocr-header {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid #e5e5e5;
    color: #409eff;
    font-size: 14px;
    font-weight: 500;

    i {
      font-size: 16px;
    }

    .processing-time {
      font-size: 12px;
      color: #999;
      font-weight: normal;
      margin-left: 4px;
    }

    .print-btns {
      display: inline-flex;
      align-items: center;
      gap: 12px;
    }

    .print-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 8px 16px;
      font-size: 13px;
      font-weight: 500;
      color: #fff;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border-radius: 20px;
      cursor: pointer;
      transition: all 0.3s ease;
      box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
      position: relative;
      overflow: hidden;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
        transition: left 0.5s ease;
      }

      i {
        font-size: 15px;
        transition: transform 0.3s ease;
      }

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
        
        &::before {
          left: 100%;
        }

        i {
          transform: scale(1.1);
        }
      }

      &:active {
        transform: translateY(0) scale(0.98);
        box-shadow: 0 2px 6px rgba(102, 126, 234, 0.3);
      }

      &:last-child {
        background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
        box-shadow: 0 2px 8px rgba(245, 87, 108, 0.3);

        &:hover {
          box-shadow: 0 4px 15px rgba(245, 87, 108, 0.4);
        }

        &:active {
          box-shadow: 0 2px 6px rgba(245, 87, 108, 0.3);
        }
      }
    }

    .ocr-actions {
      margin-left: auto;
      display: flex;
      gap: 12px;

      .action-btn {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 4px 10px;
        font-size: 13px;
        color: #576b95;
        background-color: #f0f2f5;
        border-radius: 4px;
        cursor: pointer;
        transition: all 0.3s;

        i {
          font-size: 14px;
        }

        &:hover {
          background-color: #e0e4ea;
          color: #409eff;
        }

        &:active {
          transform: scale(0.95);
        }
      }
    }
  }

  .ocr-empty {
    text-align: center;
    padding: 32px 16px;
    color: #999;

    i {
      font-size: 48px;
      color: #d9d9d9;
      margin-bottom: 12px;
    }

    p {
      font-size: 14px;
      margin: 0;
    }
  }

  .ocr-items {
    .ocr-items-title {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 14px;
      color: #409eff;
      font-weight: 600;
      margin-bottom: 12px;
      padding: 8px 12px;
      background-color: #e6f7ff;
      border-radius: 6px;

      i {
        font-size: 16px;
      }
    }

    .ocr-item {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 12px;
      padding: 12px;
      margin-bottom: 10px;
      background-color: #f9f9f9;
      border-radius: 6px;
      border-left: 3px solid #409eff;

      &:last-child {
        margin-bottom: 0;
      }

      .ocr-item-number {
        flex-shrink: 0;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #409eff;
        color: #fff;
        border-radius: 50%;
        font-size: 12px;
        font-weight: 600;
      }

      .ocr-text {
        flex: 1;
        font-size: 14px;
        line-height: 1.8;
        color: #333;
        word-break: break-word;

        /deep/ mjx-container {
          margin: 4px 0;
          overflow-x: auto;
          overflow-y: hidden;
        }
      }

      .ocr-image-icon {
        flex-shrink: 0;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #409eff;
        border-radius: 50%;
        cursor: pointer;
        transition: all 0.3s;

        &:hover {
          background-color: #66b1ff;
          transform: scale(1.1);
        }

        i {
          font-size: 16px;
          color: #fff;
        }
      }
    }
  }

  .ocr-footer {
    display: flex;
    justify-content: flex-end;
    padding: 12px 0 0;
    margin-top: 12px;
    border-top: 1px solid #ebeef5;
  }
}

// OCR 图片预览弹窗
/deep/ .ocr-image-dialog {
  .el-dialog__body {
    padding: 10px;
    text-align: center;
  }

  .ocr-preview-image {
    max-width: 100%;
    max-height: 70vh;
    object-fit: contain;
    border-radius: 4px;
  }
}

// 拍摄图片预览弹窗
/deep/ .shoot-image-dialog {
  .el-dialog__body {
    padding: 10px;
    text-align: center;
  }

  .shoot-preview-image {
    max-width: 100%;
    max-height: 70vh;
    object-fit: contain;
    border-radius: 4px;
  }
}

// 更多信息弹窗
/deep/ .more-info-dialog {
  .more-info-content {
    padding: 10px 0;

    .info-item {
      display: flex;
      align-items: center;
      padding: 12px 0;
      border-bottom: 1px solid #f0f0f0;

      &:last-child {
        border-bottom: none;
      }

      .info-label {
        font-size: 14px;
        color: #666;
        font-weight: 500;
        min-width: 100px;
      }

      .info-value {
        font-size: 14px;
        color: #333;
        flex: 1;
        word-break: break-all;
      }
    }
  }
}

// 底部输入栏
.chat-input {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background-color: #f7f7f7;
  border-top: 1px solid #d9d9d9;
  gap: 8px;

  .input-icon {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    i {
      font-size: 24px;
      color: #000;
    }
  }

  .input-box {
    flex: 1;

    input {
      width: 100%;
      height: 36px;
      padding: 8px 12px;
      border: 1px solid #d9d9d9;
      border-radius: 6px;
      background-color: #fff;
      font-size: 14px;
      outline: none;

      &:focus {
        border-color: #07c160;
      }
    }
  }
}

// Loading 容器
.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background-color: #f7f7f7;
  border-top: 1px solid #d9d9d9;

  .loading-gif {
    width: 40px;
    height: 40px;
    object-fit: contain;
  }
}

// 搜索弹窗样式
/deep/ .search-dialog {
  margin: 0 !important;
  height: 100vh;

  .el-dialog__header {
    display: none;
  }

  .el-dialog__body {
    padding: 0;
    height: 100vh;
  }
}

.search-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #ededed;
}

.search-header {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  background-color: #f7f7f7;
  gap: 10px;

  .search-box {
    flex: 1;
    display: flex;
    align-items: center;
    background-color: #fff;
    border-radius: 6px;
    padding: 8px 12px;
    gap: 8px;

    .el-icon-search {
      font-size: 18px;
      color: #999;
    }

    input {
      flex: 1;
      border: none;
      outline: none;
      font-size: 16px;

      &::placeholder {
        color: #999;
      }
    }

    .el-icon-circle-close {
      font-size: 18px;
      color: #999;
      cursor: pointer;
    }
  }

  .cancel-btn {
    font-size: 16px;
    color: #576b95;
    cursor: pointer;
  }
}

.search-tabs {
  display: flex;
  background-color: #fff;
  border-bottom: 1px solid #e5e5e5;
  overflow-x: auto;

  &::-webkit-scrollbar {
    display: none;
  }

  .tab-item {
    flex-shrink: 0;
    padding: 12px 16px;
    font-size: 14px;
    color: #666;
    cursor: pointer;
    border-bottom: 2px solid transparent;
    transition: all 0.3s;

    &.active {
      color: #07c160;
      border-bottom-color: #07c160;
    }
  }
}

.search-results {
  flex: 1;
  overflow-y: auto;
  background-color: #fff;

  .no-result {
    text-align: center;
    padding: 40px 0;
    color: #999;
    font-size: 14px;
  }

  .result-item {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid #e5e5e5;
    cursor: pointer;

    &:hover {
      background-color: #f5f5f5;
    }

    .result-avatar {
      width: 40px;
      height: 40px;
      border-radius: 4px;
      margin-right: 12px;
      flex-shrink: 0;
    }

    .result-content {
      flex: 1;
      min-width: 0;

      .result-name {
        font-size: 16px;
        color: #000;
        margin-bottom: 4px;
      }

      .result-message {
        font-size: 14px;
        color: #999;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;

        /deep/ .highlight {
          color: #07c160;
          font-weight: 500;
        }
      }
    }

    .result-date {
      font-size: 12px;
      color: #999;
      flex-shrink: 0;
      margin-left: 12px;
    }
  }
}
</style>
